'use client';
/**
 * FloatingActionStack
 * Single expandable floating launcher with DreamTwin, Feedback, First Steps, Contact Support.
 * Clean, non-overlapping, responsive, fully localized.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import {
  Brain,
  MessageSquare,
  X,
  Send,
  Bug,
  Lightbulb,
  Sparkles,
  MessageCircle,
  CheckCircle2,
  Minimize2,
  Maximize2,
  ChevronRight,
  Trash2,
  Zap,
  Mail,
  MapPin,
  LifeBuoy,
  BookOpen,
  Coins,
  AlertCircle,
} from 'lucide-react';
import { useDreamTwin } from '@/lib/dreamTwin';
import { useChat } from '@/lib/hooks/useChat';
import { DREAMTWIN_SYSTEM_PROMPT } from '@/lib/dreamTwin/types';
import { useLanguage } from '@/context/LanguageContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { applyBalanceOrRefresh } from '@/lib/client/applyServerBalance';
import {
  isConcurrentProcessingError,
  isInsufficientCreditsError,
  isRateLimitError,
  resolveCreditErrorMessage,
  formatInsufficientCreditsMessage,
  type CreditUxStrings,
} from '@/lib/ai/creditErrors';
import { toast } from 'sonner';

const DREAMTWIN_MESSAGE_COST = 1;

// ─── Types ────────────────────────────────────────────────────────────────────

type FeedbackType = 'bug' | 'suggestion' | 'feature_request' | 'general';

const FEEDBACK_TYPES: {
  id: FeedbackType;
  label: string;
  labelPt: string;
  labelEs: string;
  icon: React.ElementType;
  color: string;
  desc: string;
  descPt: string;
  descEs: string;
}[] = [
  {
    id: 'bug',
    label: 'Bug Report',
    labelPt: 'Relatório de Bug',
    labelEs: 'Reporte de Error',
    icon: Bug,
    color: 'text-red-400',
    desc: 'Something is broken or not working',
    descPt: 'Algo está quebrado ou não funciona',
    descEs: 'Algo está roto o no funciona',
  },
  {
    id: 'suggestion',
    label: 'Suggestion',
    labelPt: 'Sugestão',
    labelEs: 'Sugerencia',
    icon: Lightbulb,
    color: 'text-yellow-400',
    desc: 'An idea to improve the platform',
    descPt: 'Uma ideia para melhorar a plataforma',
    descEs: 'Una idea para mejorar la plataforma',
  },
  {
    id: 'feature_request',
    label: 'Feature Request',
    labelPt: 'Solicitação de Recurso',
    labelEs: 'Solicitud de Función',
    icon: Sparkles,
    color: 'text-primary',
    desc: "A new feature you'd love to see",
    descPt: 'Um novo recurso que você adoraria ver',
    descEs: 'Una nueva función que te gustaría ver',
  },
  {
    id: 'general',
    label: 'General Feedback',
    labelPt: 'Feedback Geral',
    labelEs: 'Comentarios Generales',
    icon: MessageCircle,
    color: 'text-mint',
    desc: 'Anything else on your mind',
    descPt: 'Qualquer outra coisa em sua mente',
    descEs: 'Cualquier otra cosa en tu mente',
  },
];

const QUICK_QUESTIONS = [
  'What should I work on next?',
  'Am I ready to launch?',
  'What is my biggest opportunity right now?',
  'Where am I wasting time?',
  'How can I increase my DreamHealth score?',
  'What is my biggest weakness?',
  'How can I automate more of my business?',
  'What marketing channel should I focus on?',
];

// ─── Message Bubble ───────────────────────────────────────────────────────────

function MessageBubble({ role, content }: { role: 'user' | 'assistant'; content: string }) {
  const isUser = role === 'user';
  return (
    <div className={`flex gap-2.5 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {!isUser && (
        <div className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mt-0.5">
          <Brain size={13} className="text-white" />
        </div>
      )}
      <div
        className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
          isUser
            ? 'bg-primary text-primary-foreground rounded-tr-sm'
            : 'bg-card border border-border text-foreground rounded-tl-sm'
        }`}
      >
        {content}
      </div>
    </div>
  );
}

// ─── DreamTwin Panel ──────────────────────────────────────────────────────────

function DreamTwinPanel({ onClose }: { onClose: () => void }) {
  const { twin, buildAIContext, addConversationMessage, clearConversation, getNextBestAction } =
    useDreamTwin();
  const { t } = useLanguage();
  const { credits, refreshSubscription } = useSubscription();
  const creditUx = (t?.creditUx ?? {}) as Partial<CreditUxStrings> & {
    costPerMessage?: string;
  };
  const creditBalance = credits?.balance ?? 0;

  const getCreditUxStrings = useCallback(
    (): CreditUxStrings => ({
      insufficient:
        creditUx.insufficient ?? 'You need {required} {requiredLabel}, but you have {balance}.',
      concurrentProcessing:
        creditUx.concurrentProcessing ?? 'Another generation is already processing. Please wait.',
      generationFailed: creditUx.generationFailed ?? 'Generation failed. Please try again.',
      creditsRestored: creditUx.creditsRestored ?? 'Your credits were restored.',
      upgradePricing: creditUx.upgradePricing ?? 'View Pricing',
      partialBusinessBuilder:
        creditUx.partialBusinessBuilder ??
        'Your business plan was created successfully and used 1 credit. We could not generate the additional brand and content assets. Your balance has been refreshed, and you can try generating the missing assets again later.',
      currentBalance: creditUx.currentBalance ?? 'Current balance',
      generationCost: creditUx.generationCost ?? 'Generation cost',
      creditSingular: creditUx.creditSingular ?? 'credit',
      creditPlural: creditUx.creditPlural ?? 'credits',
    }),
    [creditUx]
  );

  const [isExpanded, setIsExpanded] = useState(false);
  const [input, setInput] = useState('');
  const [localMessages, setLocalMessages] = useState<
    Array<{ role: 'user' | 'assistant'; content: string }>
  >([]);
  const [showQuickQuestions, setShowQuickQuestions] = useState(true);
  const [actionError, setActionError] = useState<{ message: string; showPricing: boolean } | null>(
    null
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastHandledErrorRef = useRef<Error | null>(null);
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-4o', true);
  const strings = getCreditUxStrings();
  const costPerMessageTemplate = creditUx.costPerMessage ?? '{cost} {costLabel}/message';
  const costLabel = DREAMTWIN_MESSAGE_COST === 1 ? strings.creditSingular : strings.creditPlural;
  const costLabelText = costPerMessageTemplate
    .replace('{cost}', String(DREAMTWIN_MESSAGE_COST))
    .replace('{costLabel}', costLabel);

  useEffect(() => {
    if (!error || error === lastHandledErrorRef.current) return;
    lastHandledErrorRef.current = error;

    if (
      isInsufficientCreditsError(error) ||
      isConcurrentProcessingError(error) ||
      isRateLimitError(error)
    ) {
      const resolved = resolveCreditErrorMessage(error, strings, {
        required: DREAMTWIN_MESSAGE_COST,
        balance: creditBalance,
      });
      setActionError(resolved);
      toast.error(resolved.message);
      return;
    }

    setActionError({
      message: strings.generationFailed,
      showPricing: false,
    });
    toast.error(strings.generationFailed);
    // Stream may have refunded without a balance event — refresh once.
    applyBalanceOrRefresh(undefined, refreshSubscription);
  }, [error, strings, creditBalance, refreshSubscription]);

  const lastResponseRef = useRef('');
  useEffect(() => {
    if (!isLoading && response && response !== lastResponseRef.current) {
      lastResponseRef.current = response;
      setLocalMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant')
          return [...prev.slice(0, -1), { role: 'assistant', content: response }];
        return [...prev, { role: 'assistant', content: response }];
      });
      addConversationMessage('assistant', response);
    }
  }, [response, isLoading, addConversationMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [localMessages, isLoading]);

  useEffect(() => {
    if (twin?.conversationHistory?.length && localMessages.length === 0) {
      setLocalMessages(twin.conversationHistory);
      setShowQuickQuestions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twin?.conversationHistory]);

  const buildMessages = React.useCallback(
    (userMessage: string) => {
      const context = buildAIContext();
      const systemContent = context
        ? `${DREAMTWIN_SYSTEM_PROMPT}\n\n${context}`
        : DREAMTWIN_SYSTEM_PROMPT;
      const history = localMessages
        .slice(-10)
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));
      return [
        { role: 'system' as const, content: systemContent },
        ...history,
        { role: 'user' as const, content: userMessage },
      ];
    },
    [buildAIContext, localMessages]
  );

  const handleSend = React.useCallback(
    (message?: string) => {
      const text = (message ?? input).trim();
      if (!text || isLoading) return;

      if (creditBalance < DREAMTWIN_MESSAGE_COST) {
        const msg = formatInsufficientCreditsMessage(
          strings,
          DREAMTWIN_MESSAGE_COST,
          creditBalance
        );
        setActionError({ message: msg, showPricing: true });
        toast.error(msg);
        return;
      }

      const userMsg = { role: 'user' as const, content: text };
      setLocalMessages((prev) => [...prev, userMsg]);
      addConversationMessage('user', text);
      setInput('');
      setShowQuickQuestions(false);
      setActionError(null);
      lastHandledErrorRef.current = null;
      lastResponseRef.current = '';
      setLocalMessages((prev) => [...prev, { role: 'assistant', content: '' }]);
      // Balance applied once on stream done inside getStreamingChatCompletion.
      sendMessage(buildMessages(text), { max_completion_tokens: 600 });
    },
    [input, isLoading, buildMessages, sendMessage, addConversationMessage, creditBalance, strings]
  );

  const handleClear = React.useCallback(() => {
    clearConversation();
    setLocalMessages([]);
    setShowQuickQuestions(true);
    lastResponseRef.current = '';
  }, [clearConversation]);

  const businessName = twin?.identity?.businessName || 'your business';
  const nextAction = getNextBestAction();

  return (
    <div
      className={`flex flex-col bg-background border border-border rounded-2xl shadow-2xl shadow-black/40 transition-all duration-300 ${
        isExpanded ? 'w-[420px] h-[600px]' : 'w-[340px] sm:w-[360px] h-[480px] sm:h-[500px]'
      }`}
    >
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-gradient-to-r from-primary/5 to-accent/5 rounded-t-2xl shrink-0">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shrink-0">
          <Brain size={15} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-700 text-foreground">DreamTwin™</span>
            <span className="w-1.5 h-1.5 bg-mint rounded-full animate-pulse" />
          </div>
          <p className="text-xs text-muted-foreground truncate">
            AI Business Partner · {businessName}
          </p>
          <p className="text-[10px] text-muted-foreground/80 mt-0.5">
            {costLabelText} · {strings.currentBalance}: {creditBalance}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {localMessages.length > 0 && (
            <button
              onClick={handleClear}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
              title="Clear conversation"
            >
              <Trash2 size={13} />
            </button>
          )}
          <button
            onClick={() => setIsExpanded((e) => !e)}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
          >
            {isExpanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors"
          >
            <X size={13} />
          </button>
        </div>
      </div>
      {actionError && (
        <div
          role="alert"
          className="px-4 py-2 border-b border-amber-500/20 bg-amber-500/10 flex items-start justify-between gap-2 shrink-0"
        >
          <div className="flex items-start gap-1.5 min-w-0">
            <AlertCircle size={12} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-200 leading-relaxed">{actionError.message}</p>
          </div>
          {actionError.showPricing && (
            <Link
              href="/pricing"
              className="text-[11px] font-600 text-primary hover:underline shrink-0"
            >
              {strings.upgradePricing}
            </Link>
          )}
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
        {localMessages.length === 0 && showQuickQuestions && (
          <div className="space-y-3">
            <div className="flex gap-2.5">
              <div className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mt-0.5">
                <Brain size={13} className="text-white" />
              </div>
              <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-sm leading-relaxed text-foreground max-w-[82%]">
                {twin?.identity?.businessName
                  ? `I know your business well — ${twin.identity.businessName}. What would you like to work on today?`
                  : "I'm your permanent AI Business Partner. I remember everything about your business and I'm here to help you grow. What's on your mind?"}
              </div>
            </div>
            {nextAction && (
              <button
                onClick={() => handleSend(`Tell me more about: ${nextAction.title}`)}
                className="w-full flex items-start gap-2.5 p-3 rounded-xl bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors text-left"
              >
                <Zap size={14} className="text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-600 text-primary mb-0.5">Highest Impact Right Now</p>
                  <p className="text-xs text-foreground">{nextAction.title}</p>
                </div>
              </button>
            )}
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground px-1">Quick questions:</p>
              {QUICK_QUESTIONS.slice(0, 4).map((q) => (
                <button
                  key={q}
                  onClick={() => handleSend(q)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-muted/20 hover:bg-muted/40 border border-border/50 text-left transition-colors group"
                >
                  <ChevronRight
                    size={12}
                    className="text-muted-foreground group-hover:text-primary transition-colors shrink-0"
                  />
                  <span className="text-xs text-foreground">{q}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {localMessages
          .filter((m) => m.content)
          .map((msg, i) => (
            <MessageBubble key={i} role={msg.role} content={msg.content} />
          ))}
        {isLoading && (
          <div className="flex gap-2.5">
            <div className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mt-0.5">
              <Brain size={13} className="text-white" />
            </div>
            <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-3.5 py-2.5">
              <div className="flex gap-1 items-center">
                <span
                  className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce"
                  style={{ animationDelay: '150ms' }}
                />
                <span
                  className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce"
                  style={{ animationDelay: '300ms' }}
                />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="px-3 pb-3 pt-2 border-t border-border shrink-0">
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1.5 px-0.5">
          <Coins size={10} className="text-primary" />
          <span>{costLabelText}</span>
          <span>·</span>
          <span>
            {strings.currentBalance}: {creditBalance}
          </span>
        </div>
        <div className="flex gap-2 items-center bg-muted/20 border border-border rounded-xl px-3 py-2">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask your DreamTwin anything..."
            disabled={isLoading}
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none min-w-0 disabled:opacity-50"
          />
          <button
            onClick={() => handleSend()}
            disabled={!input.trim() || isLoading}
            className="shrink-0 w-7 h-7 rounded-lg bg-primary flex items-center justify-center disabled:opacity-40 hover:bg-primary/90 transition-colors"
          >
            <Send size={12} className="text-primary-foreground" />
          </button>
        </div>
        <p className="text-center text-[10px] text-muted-foreground/50 mt-1.5">
          DreamTwin™ · Powered by AIMaker Studio
        </p>
      </div>
    </div>
  );
}

// ─── Feedback Panel ───────────────────────────────────────────────────────────

function FeedbackPanel({ onClose }: { onClose: () => void }) {
  const { language } = useLanguage();
  const [type, setType] = useState<FeedbackType>('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const getLabel = (ft: (typeof FEEDBACK_TYPES)[0]) =>
    language === 'pt' ? ft.labelPt : language === 'es' ? ft.labelEs : ft.label;
  const getDesc = (ft: (typeof FEEDBACK_TYPES)[0]) =>
    language === 'pt' ? ft.descPt : language === 'es' ? ft.descEs : ft.desc;
  const selectedType = FEEDBACK_TYPES.find((t) => t.id === type)!;

  const title =
    language === 'pt' ?'Enviar Feedback'
      : language === 'es' ?'Enviar Comentarios' :'Send Feedback';
  const feedbackTypeLabel =
    language === 'pt' ?'Tipo de Feedback'
      : language === 'es' ?'Tipo de Comentario' :'Feedback Type';
  const yourMessageLabel =
    language === 'pt' ? 'Sua Mensagem' : language === 'es' ? 'Tu Mensaje' : 'Your Message';
  const sendingLabel =
    language === 'pt' ? 'Enviando...' : language === 'es' ? 'Enviando...' : 'Sending...';
  const sendLabel =
    language === 'pt' ?'Enviar Feedback'
      : language === 'es' ?'Enviar Comentarios' :'Send Feedback';
  const thankYouLabel =
    language === 'pt' ? 'Obrigado!' : language === 'es' ? '¡Gracias!' : 'Thank you!';
  const receivedLabel =
    language === 'pt' ?'Obrigado. Seu feedback foi enviado com sucesso.'
      : language === 'es' ?'Gracias. Tus comentarios se enviaron correctamente.' :'Thank you. Your feedback was submitted successfully.';
  const placeholderLabel =
    language === 'pt'
      ? `Descreva seu ${getLabel(selectedType).toLowerCase()}...`
      : language === 'es'
        ? `Describe tu ${getLabel(selectedType).toLowerCase()}...`
        : `Describe your ${selectedType.label.toLowerCase()}...`;

  const handleSubmit = async () => {
    if (submitting) return;
    if (!message.trim()) {
      toast.error(
        language === 'pt' ?'Por favor, insira seu feedback'
          : language === 'es' ?'Por favor, ingresa tus comentarios' :'Please enter your feedback'
      );
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          message: message.trim(),
          pagePath: typeof window !== 'undefined' ? window.location.pathname : null,
          locale: language,
        }),
      });
      let data: { success?: boolean; error?: string } = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (!res.ok || !data.success) {
        throw new Error(
          data.error ||
            (language === 'pt' ?'Falha ao enviar feedback. Tente novamente.'
              : language === 'es' ?'Error al enviar comentarios. Inténtalo de nuevo.' :'Failed to submit feedback. Please try again.')
        );
      }
      setSubmitted(true);
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setMessage('');
        setType('general');
        setSubmitError(null);
      }, 2500);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : language === 'pt' ?'Falha ao enviar feedback. Tente novamente.'
            : language === 'es' ?'Error al enviar comentarios. Inténtalo de nuevo.' :'Failed to submit feedback. Please try again.';
      setSubmitError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="w-[300px] sm:w-80 rounded-2xl bg-card border border-border shadow-2xl overflow-hidden"
      style={{ animation: 'fabPanelIn 0.2s ease-out' }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2">
          <MessageSquare size={15} className="text-primary" />
          <p className="text-sm font-600 text-foreground">{title}</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
      {submitted ? (
        <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
          <div className="w-12 h-12 rounded-full bg-mint/10 flex items-center justify-center mb-3">
            <CheckCircle2 size={24} className="text-mint" />
          </div>
          <p className="text-base font-700 text-foreground mb-1">{thankYouLabel}</p>
          <p className="text-sm text-muted-foreground">{receivedLabel}</p>
        </div>
      ) : (
        <div className="p-4 flex flex-col gap-4">
          <div>
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-2">
              {feedbackTypeLabel}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {FEEDBACK_TYPES.map((ft) => {
                const FtIcon = ft.icon;
                return (
                  <button
                    key={ft.id}
                    onClick={() => setType(ft.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-500 transition-all border text-left ${type === ft.id ? 'bg-primary/10 border-primary/30 text-primary' : 'border-border text-muted-foreground hover:border-border/80 hover:text-foreground hover:bg-muted/20'}`}
                  >
                    <FtIcon size={13} className={type === ft.id ? 'text-primary' : ft.color} />
                    {getLabel(ft)}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="text-xs font-600 text-muted-foreground uppercase tracking-wider mb-2">
              {yourMessageLabel}
            </p>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="input-dark w-full px-3 py-2.5 rounded-lg text-sm resize-none"
              placeholder={placeholderLabel}
            />
            <p className="text-[10px] text-muted-foreground mt-1">{getDesc(selectedType)}</p>
          </div>
          {submitError && (
            <div role="alert" className="rounded-lg border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs text-red-200">
              {submitError}
            </div>
          )}
          <button
            onClick={handleSubmit}
            disabled={submitting || !message.trim()}
            className="btn-primary w-full py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {sendingLabel}
              </>
            ) : (
              <>
                <Send size={13} />
                {sendLabel}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main FloatingActionStack ─────────────────────────────────────────────────

type ActivePanel = 'dreamtwin' | 'feedback' | null;

interface QuickAction {
  id: string;
  icon: React.ElementType;
  labels: { en: string; pt: string; es: string };
  color: string;
  bg: string;
  action: 'panel' | 'link';
  panel?: ActivePanel;
  href?: string;
  badge?: boolean;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'learn',
    icon: BookOpen,
    labels: { en: 'Learn Center', pt: 'Central de Aprendizado', es: 'Centro de Aprendizaje' },
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
    action: 'link',
    href: '/learn-center',
  },
  {
    id: 'tour',
    icon: MapPin,
    labels: { en: 'Product Tour', pt: 'Tour do Produto', es: 'Tour del Producto' },
    color: 'text-green-400',
    bg: 'bg-green-400/10',
    action: 'link',
    href: '/welcome?tour=true',
  },
  {
    id: 'feedback',
    icon: MessageSquare,
    labels: { en: 'Feedback', pt: 'Feedback', es: 'Comentarios' },
    color: 'text-yellow-400',
    bg: 'bg-yellow-400/10',
    action: 'panel',
    panel: 'feedback',
  },
  {
    id: 'dreamtwin',
    icon: Brain,
    labels: { en: 'DreamTwin Assistant', pt: 'Assistente DreamTwin™', es: 'Asistente DreamTwin™' },
    color: 'text-primary',
    bg: 'bg-primary/10',
    action: 'panel',
    panel: 'dreamtwin',
    badge: true,
  },
  {
    id: 'contact',
    icon: Mail,
    labels: { en: 'Contact Support', pt: 'Contatar Suporte', es: 'Contactar Soporte' },
    color: 'text-mint',
    bg: 'bg-mint/10',
    action: 'link',
    href: '/contact',
  },
];

export default function FloatingActionStack() {
  const [mounted, setMounted] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const { language } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!expanded && !activePanel) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setExpanded(false);
        setActivePanel(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [expanded, activePanel]);

  if (!mounted) return null;

  const getLabel = (a: QuickAction) => {
    if (language === 'pt') return a.labels.pt;
    if (language === 'es') return a.labels.es;
    return a.labels.en;
  };

  const handleAction = (action: QuickAction) => {
    if (action.action === 'panel') {
      const panel = action.panel as ActivePanel;
      setActivePanel((prev) => (prev === panel ? null : panel));
      setExpanded(false);
    }
  };

  const mainLabel = {
    open:
      language === 'pt' ?'Ajuda & Suporte'
        : language === 'es' ?'Ayuda & Soporte' :'Help & Support',
    close: language === 'pt' ? 'Fechar' : language === 'es' ? 'Cerrar' : 'Close',
  };

  return (
    <>
      <style>{`
        @keyframes fabPanelIn { from { opacity: 0; transform: translateY(10px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes fabItemIn { from { opacity: 0; transform: translateX(12px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>

      <div
        ref={containerRef}
        className="fixed bottom-6 right-4 sm:right-6 z-[9999] flex flex-col items-end gap-3 pointer-events-none"
        style={{ maxWidth: 'min(calc(100vw - 1rem), 420px)' }}
      >
        {/* Active panel — appears above the FAB, scrollable if needed */}
        {activePanel && (
          <div
            className="pointer-events-auto flex flex-col items-end overflow-y-auto"
            style={{
              animation: 'fabPanelIn 0.2s ease-out',
              maxHeight: 'calc(100vh - 160px)',
              maxWidth: '100%',
            }}
          >
            {activePanel === 'dreamtwin' && <DreamTwinPanel onClose={() => setActivePanel(null)} />}
            {activePanel === 'feedback' && <FeedbackPanel onClose={() => setActivePanel(null)} />}
          </div>
        )}

        {/* Expanded quick actions menu */}
        {expanded && !activePanel && (
          <div className="pointer-events-auto flex flex-col items-end gap-3 mb-1">
            {QUICK_ACTIONS.map((action, i) => {
              const ActionIcon = action.icon;
              const label = getLabel(action);
              const itemStyle = { animation: `fabItemIn 0.15s ease-out ${i * 50}ms both` };

              if (action.action === 'link' && action.href) {
                return (
                  <Link
                    key={action.id}
                    href={action.href}
                    onClick={() => setExpanded(false)}
                    style={itemStyle}
                    className="pointer-events-auto flex items-center gap-3 pl-3 pr-4 py-2.5 rounded-full bg-card border border-border/80 shadow-lg hover:border-primary/40 hover:shadow-xl transition-all group"
                  >
                    <div
                      className={`w-7 h-7 rounded-full ${action.bg} flex items-center justify-center shrink-0`}
                    >
                      <ActionIcon size={14} className={action.color} />
                    </div>
                    <span className="text-sm font-600 text-foreground group-hover:text-primary transition-colors whitespace-nowrap">
                      {label}
                    </span>
                  </Link>
                );
              }
              return (
                <button
                  key={action.id}
                  onClick={() => handleAction(action)}
                  style={itemStyle}
                  className="pointer-events-auto flex items-center gap-3 pl-3 pr-4 py-2.5 rounded-full bg-card border border-border/80 shadow-lg hover:border-primary/40 hover:shadow-xl transition-all group"
                >
                  <div
                    className={`w-7 h-7 rounded-full ${action.bg} flex items-center justify-center shrink-0 relative`}
                  >
                    <ActionIcon size={14} className={action.color} />
                    {action.badge && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-mint rounded-full animate-pulse" />
                    )}
                  </div>
                  <span className="text-sm font-600 text-foreground group-hover:text-primary transition-colors whitespace-nowrap">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Main FAB button */}
        <button
          onClick={() => {
            if (activePanel) {
              setActivePanel(null);
              return;
            }
            setExpanded((e) => !e);
          }}
          className={`pointer-events-auto flex items-center gap-2.5 px-4 py-3 rounded-full shadow-xl transition-all font-700 text-sm select-none ${
            expanded || activePanel
              ? 'bg-card border border-border text-foreground shadow-md'
              : 'bg-gradient-to-r from-primary to-accent text-white shadow-primary/30 hover:shadow-primary/50 hover:scale-105'
          }`}
          aria-label={mainLabel.open}
        >
          {expanded || activePanel ? (
            <X size={16} />
          ) : (
            <div className="relative">
              <LifeBuoy size={16} />
            </div>
          )}
          <span className="hidden sm:inline">
            {expanded || activePanel ? mainLabel.close : mainLabel.open}
          </span>
        </button>
      </div>
    </>
  );
}
